/**
 * 受領書のPDF生成(2026-08-26)。
 * 受領書ページの「PDFで保存」と、押印式の電子署名(署名済み受領書の生成)で共用する。
 * 日本語はcanvasで描画(OSのフォントを利用)し、A4のPDFに埋め込む。
 * 押印は「苗字」の丸い朱印風スタンプとして描画する
 */
import { PDFDocument } from 'pdf-lib';
import { formatYen } from '@core/lib/money';
import type { Transaction } from '@core/lib/types';

export type ReceiptRenderParams = {
  transaction: Transaction;
  companyName: string;
  /** 受領者欄のラベル(本人 / 支払先) */
  receiverLabel: string;
  receiverName: string;
  /** 但し書きに添える注記(仮払い金・立替精算金など)。無指定なら注記なし */
  kindNote?: string | null;
  /** 受領日。null なら空欄(手書き用) */
  receiptDate?: Date | null;
  /** 2行目のラベル(内部=支払先)。null なら行ごと出さない(支払先用は署名=受領者のため・2026-08-27) */
  counterpartyLabel?: string | null;
  /** 2行目の名前。null なら空欄の下線のみ(手書き用) */
  counterpartyName?: string | null;
  /** 署名欄に印字する氏名。null なら空欄(手書き用) */
  signatureName?: string | null;
  /** 押印(苗字の朱印)。null なら「印」の空欄 */
  stampName?: string | null;
  /** 手書きサイン画像(支払先のサインパッド)。署名欄の上に描画する */
  signatureImage?: HTMLImageElement | null;
};

/** 朱印風の丸スタンプを描く(画面表示のスタイルと合わせる) */
function drawSeal(ctx: CanvasRenderingContext2D, cx: number, cy: number, r: number, name: string) {
  ctx.save();
  ctx.strokeStyle = '#C0392B';
  ctx.lineWidth = 6;
  ctx.beginPath();
  ctx.arc(cx, cy, r, 0, Math.PI * 2);
  ctx.stroke();
  ctx.fillStyle = '#C0392B';
  const size = name.length <= 2 ? r * 0.62 : r * 0.46;
  ctx.font = `bold ${size}px "Hiragino Mincho ProN", "Yu Mincho", serif`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(name, cx, cy + 2);
  ctx.restore();
}

/** A4 150dpi相当のキャンバスに受領書を描く */
export function drawReceiptCanvas(p: ReceiptRenderParams): HTMLCanvasElement {
  const { transaction: t } = p;
  const W = 1240;
  const H = 1754;
  const canvas = document.createElement('canvas');
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext('2d')!;
  const serif = '"Hiragino Mincho ProN", "Yu Mincho", "MS Mincho", serif';
  const sans = '"Hiragino Sans", "Yu Gothic", "Meiryo", sans-serif';

  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, W, H);
  ctx.fillStyle = '#1a1a1a';

  const now = new Date();
  const dateLabel = (d: Date) => `${d.getFullYear()}年${d.getMonth() + 1}月${d.getDate()}日`;

  // 右上: 管理番号・発行日
  ctx.font = `26px ${sans}`;
  ctx.fillStyle = '#555555';
  ctx.textAlign = 'right';
  ctx.fillText(`管理番号: ${t.code ?? ''}`, W - 100, 130);
  ctx.fillText(`発行日: ${dateLabel(now)}`, W - 100, 170);

  // タイトル
  ctx.fillStyle = '#1a1a1a';
  ctx.textAlign = 'center';
  ctx.font = `72px ${serif}`;
  ctx.fillText('受  領  書', W / 2, 300);

  // 宛名
  ctx.textAlign = 'left';
  ctx.font = `38px ${serif}`;
  ctx.fillText(`${p.companyName} 御中`, 120, 420);
  ctx.strokeStyle = '#1a1a1a';
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.moveTo(120, 440);
  ctx.lineTo(740, 440);
  ctx.stroke();

  // 金額枠
  ctx.lineWidth = 4;
  ctx.strokeRect(200, 500, W - 400, 110);
  ctx.font = `32px ${serif}`;
  ctx.fillText('金 額', 240, 570);
  ctx.font = `bold 54px ${serif}`;
  ctx.fillText(`¥${formatYen(t.amount)} −`, 420, 578);
  ctx.font = `26px ${serif}`;
  ctx.fillText('(税込)', 960, 570);

  // 但し書き・受領文
  ctx.font = `32px ${serif}`;
  const tadashi = `但し: ${t.description ?? ''}${p.kindNote ? `(${p.kindNote})` : ''}`;
  ctx.fillText(tadashi.slice(0, 40), 120, 700);
  ctx.fillText('上記の金額を、正に受領いたしました。', 120, 760);

  // 受領日(当日)・1行目(内部=受領者(本人) / 支払先用=支払い者)・
  // 2行目(内部=支払先 / 支払先用=受領者)(2026-08-27 改称・記入日は廃止)
  ctx.fillText(
    p.receiptDate ? `受領日: ${dateLabel(p.receiptDate)}` : '受領日:        年     月     日',
    120,
    880,
  );
  ctx.fillText(`${p.receiverLabel}: ${p.receiverName}`, 120, 945);
  if (!p.receiverName) {
    // 空欄(手書き)の場合は下線を引く
    ctx.beginPath();
    ctx.moveTo(120 + ctx.measureText(`${p.receiverLabel}: `).width, 957);
    ctx.lineTo(760, 957);
    ctx.stroke();
  }
  // 2行目(内部=支払先)。支払先用は行なし(受領者=署名する人のため・2026-08-27)
  if (p.counterpartyLabel) {
    ctx.fillText(`${p.counterpartyLabel}: ${p.counterpartyName ?? ''}`, 120, 1010);
    if (!p.counterpartyName) {
      ctx.beginPath();
      ctx.moveTo(120 + ctx.measureText(`${p.counterpartyLabel}: `).width, 1022);
      ctx.lineTo(760, 1022);
      ctx.stroke();
    }
  }

  // 署名欄(下線)+氏名印字+押印
  ctx.fillText('署名:', 120, 1090);
  ctx.beginPath();
  ctx.moveTo(230, 1102);
  ctx.lineTo(760, 1102);
  ctx.stroke();
  if (p.signatureName) {
    ctx.font = `40px ${serif}`;
    ctx.fillText(p.signatureName, 260, 1092);
    ctx.font = `32px ${serif}`;
  }
  if (p.signatureImage) {
    // 手書きサインを署名線の上に、縦横比を保って欄内(幅500×高さ80)に収める(2026-08-26 はみ出し修正)
    const img = p.signatureImage;
    const scale = Math.min(500 / img.width, 80 / img.height);
    const w = img.width * scale;
    const h = img.height * scale;
    ctx.drawImage(img, 250, 1102 - h + 6, w, h);
  }
  if (p.stampName) {
    drawSeal(ctx, 860, 1070, 62, p.stampName);
  } else {
    ctx.fillStyle = '#555555';
    ctx.font = `24px ${sans}`;
    ctx.fillText('印', 848, 1078);
    ctx.fillStyle = '#1a1a1a';
    ctx.font = `32px ${serif}`;
  }

  // 記入日欄は廃止(2026-08-27 指示)

  // フッター
  ctx.strokeStyle = '#bbbbbb';
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.moveTo(100, H - 160);
  ctx.lineTo(W - 100, H - 160);
  ctx.stroke();
  ctx.fillStyle = '#555555';
  ctx.font = `22px ${sans}`;
  ctx.textAlign = 'left';
  const stampNote = p.stampName ? ` / 電子署名(押印): ${p.receiverName} ${now.toLocaleString('ja-JP')}` : '';
  ctx.fillText(`Nexus Gate 発行 / 支払(予定)日: ${t.payment_date}${stampNote}`, 120, H - 110);

  return canvas;
}

/** データURLから画像を読み込む(手書きサインの描画用) */
export function loadImage(dataUrl: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('署名画像の読み込みに失敗しました'));
    img.src = dataUrl;
  });
}

/** 受領書のPDFファイルを作る */
export async function buildReceiptPdfFile(p: ReceiptRenderParams, fileName: string): Promise<File> {
  const canvas = drawReceiptCanvas(p);
  const blob = await new Promise<Blob>((resolve, reject) =>
    canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('画像化に失敗しました'))), 'image/png'),
  );
  const png = new Uint8Array(await blob.arrayBuffer());
  const doc = await PDFDocument.create();
  const image = await doc.embedPng(png);
  const page = doc.addPage([595.28, 841.89]);
  page.drawImage(image, { x: 0, y: 0, width: 595.28, height: 841.89 });
  const bytes = await doc.save();
  return new File([new Uint8Array(bytes)], fileName, { type: 'application/pdf' });
}

/** PDFファイルをブラウザからダウンロード保存する */
export function downloadFile(file: File) {
  const url = URL.createObjectURL(file);
  const a = document.createElement('a');
  a.href = url;
  a.download = file.name;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}
